import React, {
  PropsWithChildren,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { OptionType } from 'theme';

export type DataDrivenFiltersContextType = {
  /**
   * Optional debounce time for options in milliseconds.
   * This can be used to limit the rate at which options are processed.
   */
  optionsDebounceTime?: number;
  /**
   * A function that determines if a given option should be included based on the input value.
   *
   * @param option - The option to be evaluated.
   * @param inputValue - The current input value to filter options against.
   * @returns A boolean indicating whether the option should be included.
   */
  optionsFilterOption?: (option: OptionType, inputValue: string) => boolean;
};
export const DataDrivenFiltersContext =
  createContext<DataDrivenFiltersContextType>(null);

export function DataDrivenFiltersProvider({
  children,
  optionsDebounceTime,
  optionsFilterOption = null,
}: PropsWithChildren<DataDrivenFiltersContextType>) {
  const value = useMemo(
    () => ({
      optionsDebounceTime,
      optionsFilterOption,
    }),
    [optionsDebounceTime, optionsFilterOption]
  );

  return (
    <DataDrivenFiltersContext.Provider value={value}>
      {children}
    </DataDrivenFiltersContext.Provider>
  );
}

export const useDataDrivenFilterContext = () =>
  useContext(DataDrivenFiltersContext);
